/**
 * Modal & ConfirmModal — Magnum-grade shared SDK components
 *
 * Accessibility: WCAG 2.1 AA — focus trap, focus restore, aria-labelledby,
 *   aria-describedby, unique IDs per instance, prefers-reduced-motion.
 * Theme: 100% CSS variables — zero hardcoded colors.
 * Animation: enter/exit via transform+opacity only (no layout thrash), 250ms.
 * API: superset of every plugin-local Modal that existed before this.
 */

const React = (globalThis as any).React;
const { useEffect, useRef, useState, useCallback, useMemo } = React;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Named sizes or a pixel number for free-form width */
  size?: 'sm' | 'md' | 'lg' | 'xl' | number;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  /** i18n — label for the close button (default "Close modal") */
  closeLabel?: string;
  /** CSS selector for the element to focus on open (e.g. "#name-input") */
  initialFocus?: string;
  /** Headless mode: keeps focus trap + ARIA but hides header chrome */
  headless?: boolean;
  /** Override default modal-content test id (UI-TEST-03 plugin prefix) */
  testId?: string;
}

export interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description?: string;
  children?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'primary' | 'danger';
  loading?: boolean;
  size?: ModalProps['size'];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let modalCounter = 0;
function useModalId() {
  const idRef = useRef('') as { current: string };
  if (!idRef.current) {
    modalCounter += 1;
    idRef.current = `sui-modal-${modalCounter}`;
  }
  return idRef.current;
}

const SIZE_MAP: Record<string, string> = {
  sm: '400px',
  md: '600px',
  lg: '800px',
  xl: '1200px',
};

function resolveMaxWidth(size: ModalProps['size']): string {
  if (typeof size === 'number') return `${size}px`;
  return SIZE_MAP[size || 'md'];
}

function hasRenderableChildren(children: React.ReactNode): boolean {
  if (children == null || children === false) return false;
  if (Array.isArray(children)) return children.some((child) => hasRenderableChildren(child));
  return true;
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const candidates = Array.from(
    container.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
  );
  return candidates.filter(
    (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true'
  );
}

const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
};

let scrollLockCount = 0;
let savedOverflow = '';

function lockScroll() {
  if (scrollLockCount === 0) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  scrollLockCount++;
}

function unlockScroll() {
  scrollLockCount--;
  if (scrollLockCount <= 0) {
    scrollLockCount = 0;
    document.body.style.overflow = savedOverflow;
  }
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnBackdropClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  closeLabel = 'Close modal',
  initialFocus,
  headless = false,
  testId,
}: ModalProps) {
  const id = useModalId();
  const titleId = `${id}-title`;
  const descId = `${id}-desc`;

  const modalRef = useRef(null) as React.MutableRefObject<HTMLDivElement | null>;
  const previousFocusRef = useRef(null) as React.MutableRefObject<HTMLElement | null>;
  const hasFocusedRef = useRef(false);
  const [phase, setPhase] = useState('closed') as ['closed' | 'entering' | 'open' | 'exiting', (p: 'closed' | 'entering' | 'open' | 'exiting') => void];

  const reducedMotion = useMemo(prefersReducedMotion, []);
  const animDuration = reducedMotion ? 0 : 250;

  // --- lifecycle: open / close with animation phases ----------------------

  useEffect(() => {
    if (isOpen && phase === 'closed') {
      setPhase('entering');
      const t = setTimeout(() => setPhase('open'), animDuration);
      return () => clearTimeout(t);
    }
    if (!isOpen && (phase === 'open' || phase === 'entering')) {
      setPhase('exiting');
      const t = setTimeout(() => setPhase('closed'), animDuration);
      return () => clearTimeout(t);
    }
  }, [isOpen]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Reset focus tracking when fully closed
  useEffect(() => {
    if (phase === 'closed') {
      hasFocusedRef.current = false;
    }
  }, [phase]);

  // --- body scroll lock (ref-counted for nested modals) -------------------

  useEffect(() => {
    if (phase === 'closed') return;
    lockScroll();
    return () => { unlockScroll(); };
  }, [phase]);

  // --- focus trap + keyboard ---------------------------------------------

  useEffect(() => {
    if (phase === 'closed' || phase === 'exiting') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEscape) {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const modal = modalRef.current;
        if (!modal) return;
        const focusable = getFocusableElements(modal);
        if (focusable.length === 0) { e.preventDefault(); return; }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    if (!hasFocusedRef.current) {
      hasFocusedRef.current = true;
      previousFocusRef.current = document.activeElement as HTMLElement;
      setTimeout(() => {
        const modal = modalRef.current;
        if (!modal) return;
        if (initialFocus) {
          const target = modal.querySelector(initialFocus) as HTMLElement | null;
          if (target) { target.focus(); return; }
        }
        const focusable = getFocusableElements(modal);
        if (focusable.length > 0) focusable[0].focus();
        else modal.focus();
      }, 50);
    }

    return () => { document.removeEventListener('keydown', handleKeyDown); };
  }, [phase, closeOnEscape, onClose, initialFocus]);

  // --- restore focus when modal transitions to closed --------------------

  useEffect(() => {
    if (phase === 'closed' && previousFocusRef.current) {
      const el = previousFocusRef.current;
      previousFocusRef.current = null;
      el.focus();
    }
  }, [phase]);

  // --- backdrop click ----------------------------------------------------

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closeOnBackdropClick) {
      onClose();
    }
  }, [closeOnBackdropClick, onClose]);

  // --- render nothing when fully closed ----------------------------------

  if (phase === 'closed') return null;

  const isVisible = phase === 'open';

  // --- styles (100% CSS variables) ----------------------------------------

  const backdropStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'var(--modal-overlay, rgba(0, 0, 0, 0.6))',
    backdropFilter: isVisible ? 'blur(6px)' : 'blur(0px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '20px',
    opacity: isVisible ? 1 : 0,
    transition: reducedMotion ? 'none' : `opacity ${animDuration}ms cubic-bezier(0.4, 0, 0.2, 1), backdrop-filter ${animDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
  };

  const panelStyle: React.CSSProperties = {
    background: 'var(--modal-bg, var(--card-bg))',
    border: '1px solid var(--modal-border, var(--card-border, var(--border-color)))',
    borderRadius: 'var(--card-radius, 16px)',
    width: '100%',
    maxWidth: resolveMaxWidth(size),
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'var(--modal-shadow, var(--shadow-xl, 0 8px 32px rgba(0,0,0,0.12), 0 24px 64px rgba(0,0,0,0.15)))',
    color: 'var(--main-text)',
    outline: 'none',
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(-20px) scale(0.97)',
    transition: reducedMotion ? 'none' : `opacity ${animDuration}ms cubic-bezier(0.4, 0, 0.2, 1), transform ${animDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
  };

  const headerStyle: React.CSSProperties = {
    padding: '24px 24px 0 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
  };

  const headerDividerStyle: React.CSSProperties = {
    margin: '16px 0 0 0',
    borderTop: '1px solid var(--border-color)',
  };

  const closeBtnStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    fontSize: '20px',
    color: 'var(--main-text-secondary)',
    cursor: 'pointer',
    padding: '6px 8px',
    lineHeight: 1,
    borderRadius: 'var(--card-radius, 8px)',
    transition: 'all 150ms',
    flexShrink: 0,
  };

  const contentStyle: React.CSSProperties = {
    padding: '24px',
    overflowY: 'auto',
    flex: 1,
  };

  const footerStyle: React.CSSProperties = {
    padding: '16px 24px',
    borderTop: '1px solid var(--border-color)',
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  };

  const hasHeader = !headless && (title || showCloseButton);
  const showContent = hasRenderableChildren(children);
  const bodyStyle: React.CSSProperties = showContent
    ? contentStyle
    : { display: 'none' };

  return React.createElement('div', {
    style: backdropStyle,
    onClick: handleBackdropClick,
    'data-testid': 'modal-backdrop',
  },
    React.createElement('div', {
      ref: modalRef,
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': title ? titleId : undefined,
      'aria-describedby': description ? descId : undefined,
      style: panelStyle,
      tabIndex: -1,
      'data-testid': testId ?? 'modal-content',
    },
      // Header
      hasHeader && React.createElement('div', null,
        React.createElement('div', { style: headerStyle },
          React.createElement('div', { style: { flex: 1, minWidth: 0 } },
            title && React.createElement('h2', {
              id: titleId,
              style: {
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                color: 'var(--main-text)',
              },
            }, title),
            description && React.createElement('p', {
              id: descId,
              style: {
                margin: '4px 0 0 0',
                fontSize: '14px',
                color: 'var(--main-text-secondary)',
                lineHeight: '1.5',
              },
            }, description),
          ),
          showCloseButton && React.createElement('button', {
            onClick: onClose,
            style: closeBtnStyle,
            'aria-label': closeLabel,
            'data-testid': 'modal-close',
            onMouseEnter: (e: any) => {
              e.currentTarget.style.background = 'var(--card-hover-bg, var(--hover-bg))';
              e.currentTarget.style.color = 'var(--main-text)';
            },
            onMouseLeave: (e: any) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--main-text-secondary)';
            },
          }, '\u00D7'),
        ),
        showContent && React.createElement('div', { style: headerDividerStyle }),
      ),

      // Content — omit flex growth when there is no body (title/description/footer-only dialogs)
      showContent && React.createElement('div', { style: bodyStyle }, children),

      // Footer
      footer && React.createElement('div', { style: footerStyle }, footer),
    )
  );
}

// ---------------------------------------------------------------------------
// ConfirmModal
// ---------------------------------------------------------------------------

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  loading = false,
  size = 'sm',
}: ConfirmModalProps) {
  const [confirming, setConfirming] = useState(false);
  const isLoading = loading || confirming;

  const mountedRef = useRef(true);
  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  const handleConfirm = useCallback(async () => {
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      if (mountedRef.current) setConfirming(false);
    }
  }, [onConfirm]);

  const isPrimary = confirmVariant === 'primary';

  const confirmBtnStyle: React.CSSProperties = {
    padding: '10px 20px',
    borderRadius: 'var(--card-radius, 8px)',
    border: 'none',
    cursor: isLoading ? 'not-allowed' : 'pointer',
    fontWeight: '600',
    fontSize: '14px',
    transition: 'all 150ms',
    opacity: isLoading ? 0.7 : 1,
    background: isPrimary
      ? 'var(--button-bg, var(--main-accent))'
      : 'var(--error-color, #ef4444)',
    color: isPrimary
      ? 'var(--button-text, #fff)'
      : 'var(--error-text-contrast, #fff)',
  };

  const cancelBtnStyle: React.CSSProperties = {
    padding: '10px 20px',
    borderRadius: 'var(--card-radius, 8px)',
    border: '1px solid var(--border-color)',
    background: 'transparent',
    color: 'var(--main-text)',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '14px',
    transition: 'all 150ms',
  };

  return React.createElement(Modal, {
    isOpen,
    onClose,
    title,
    description,
    size,
    closeOnEscape: !isLoading,
    closeOnBackdropClick: !isLoading,
    footer: React.createElement(React.Fragment, null,
      React.createElement('button', {
        onClick: onClose,
        style: cancelBtnStyle,
        disabled: isLoading,
        'data-testid': 'confirm-modal-cancel',
        onMouseEnter: (e: any) => {
          if (!isLoading) e.currentTarget.style.background = 'var(--card-hover-bg, var(--hover-bg))';
        },
        onMouseLeave: (e: any) => {
          e.currentTarget.style.background = 'transparent';
        },
      }, cancelLabel),
      React.createElement('button', {
        onClick: handleConfirm,
        style: confirmBtnStyle,
        disabled: isLoading,
        'data-testid': 'confirm-modal-confirm',
        onMouseEnter: (e: any) => {
          if (!isLoading) e.currentTarget.style.opacity = '0.85';
        },
        onMouseLeave: (e: any) => {
          e.currentTarget.style.opacity = isLoading ? '0.7' : '1';
        },
      },
        isLoading
          ? React.createElement('span', {
              style: {
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              },
            },
              React.createElement('span', {
                style: {
                  display: 'inline-block',
                  width: '14px',
                  height: '14px',
                  border: '2px solid currentColor',
                  borderTopColor: 'currentColor',
                  borderRightColor: 'transparent',
                  borderBottomColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                },
              }),
              confirmLabel,
            )
          : confirmLabel,
      ),
    ),
  }, children);
}
